import React, {
  useState, useEffect, useCallback,
} from 'react';
import {
  Col, Container, Row, Spinner,
} from 'react-bootstrap';
import Button from 'components/DesignSystem/Button';
import Dialog from 'components/DesignSystem/Dialog';
import Snackbar from 'components/DesignSystem/Snackbar';
import ModelsTable from 'components/Admin/Windx/Models/ModelsTable';
import ModelsMobile from 'components/Admin/Windx/Models/Mobile';
import ModelsModal from 'components/Admin/Windx/Models/ModelsModal';
import {
  useListWindxModelsQuery,
  useDeleteWindxModelMutation,
  useUpdateWindxModelMutation,
} from 'services/api/windxModels';
import { WindxPainelOption, ModelsSortField } from 'types/Windx/Models/windxPainelOption';
import { SortDirection, SORT_ASC } from 'constants/sorting';
import useIsMobile from 'utils/hooks/useIsMobile';

// Erro do RTK Query (HTTP 4xx) traz o envelope do backend em `data`.
interface RtkError { data?: { description?: string; message?: string } }

interface Feedback { variant: 'success' | 'danger'; msg: string }

// Ordena a lista no cliente. `undefined` preserva a ordem do backend (order_by).
function sortModels(
  models: WindxPainelOption[],
  sortBy: ModelsSortField,
  sortDirection: SortDirection | undefined,
): WindxPainelOption[] {
  if (!sortDirection) return models;
  const value = (m: WindxPainelOption): string | number | boolean => {
    switch (sortBy) {
      case 'label': return m.config.label;
      case 'categoria': return m.categoria ?? '';
      case 'ativo': return m.ativo;
      case 'codigo': return m.codigo;
      case 'order_by': return m.order_by;
      default: return m.id;
    }
  };
  return [...models].sort((a, b) => {
    const av = value(a);
    const bv = value(b);
    let cmp: number;
    if (typeof av === 'string' && typeof bv === 'string') cmp = av.localeCompare(bv);
    else if (typeof av === 'boolean' && typeof bv === 'boolean') cmp = Number(av) - Number(bv);
    else cmp = (av as number) - (bv as number);
    return sortDirection === SORT_ASC ? cmp : -cmp;
  });
}

export default function AdminWindxModels() {
  const [modalOpen, setModalOpen] = useState(false);
  const [editingModel, setEditingModel] = useState<WindxPainelOption | null>(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [modelToDelete, setModelToDelete] = useState<WindxPainelOption | null>(null);
  const [deleteError, setDeleteError] = useState<string | null>(null);
  const [pendingId, setPendingId] = useState<number | null>(null);
  const [feedback, setFeedback] = useState<Feedback | null>(null);
  const [sortBy, setSortBy] = useState<ModelsSortField>('order_by');
  const [sortDirection, setSortDirection] = useState<SortDirection | undefined>(undefined);

  const { data, isLoading, isFetching } = useListWindxModelsQuery();
  const [deleteModel] = useDeleteWindxModelMutation();
  const [updateModel] = useUpdateWindxModelMutation();
  const isMobile = useIsMobile();

  // Auto-dismiss do feedback (Snackbar) depois de alguns segundos.
  useEffect(() => {
    if (!feedback) return undefined;
    const timer = setTimeout(() => setFeedback(null), 4000);
    return () => clearTimeout(timer);
  }, [feedback]);

  const handleEdit = useCallback((m: WindxPainelOption) => {
    setEditingModel(m);
    setModalOpen(true);
  }, []);

  const handleCreate = useCallback(() => {
    setEditingModel(null);
    setModalOpen(true);
  }, []);

  const handleCloseModal = useCallback(() => {
    setModalOpen(false);
    setEditingModel(null);
  }, []);

  const handleSaved = useCallback((wasEditing: boolean) => {
    setFeedback({ variant: 'success', msg: wasEditing ? 'Modelo atualizado.' : 'Modelo criado.' });
  }, []);

  const handleToggleAtivo = useCallback(async (m: WindxPainelOption) => {
    setPendingId(m.id);
    try {
      await updateModel({ id: m.id, ativo: !m.ativo }).unwrap();
      setFeedback({ variant: 'success', msg: m.ativo ? 'Modelo ocultado.' : 'Modelo exibido.' });
    } catch {
      setFeedback({ variant: 'danger', msg: 'Não foi possível alterar a visibilidade do modelo.' });
    } finally {
      setPendingId(null);
    }
  }, [updateModel]);

  const handleDeleteRequest = useCallback((m: WindxPainelOption) => {
    setModelToDelete(m);
    setDeleteError(null);
    setDeleteDialogOpen(true);
  }, []);

  const handleConfirmDelete = useCallback(async () => {
    if (!modelToDelete) return;
    try {
      await deleteModel(modelToDelete.id).unwrap();
      setDeleteDialogOpen(false);
      setModelToDelete(null);
      setFeedback({ variant: 'success', msg: 'Modelo excluído.' });
    } catch (err) {
      // Hard-delete guardado: o backend devolve 409 quando o modelo está em uso.
      const e = err as RtkError;
      setDeleteError(
        e?.data?.description
        || e?.data?.message
        || 'Não foi possível excluir o modelo.',
      );
    }
  }, [modelToDelete, deleteModel]);

  if (isLoading) {
    return <Spinner className="mt-5 mx-auto d-block" animation="border" />;
  }

  const sortedModels = data ? sortModels(data, sortBy, sortDirection) : undefined;

  return (
    <div>
      <ModelsModal
        open={modalOpen}
        onClose={handleCloseModal}
        onSuccess={handleSaved}
        model={editingModel}
      />
      <Dialog
        open={deleteDialogOpen}
        dismiss={() => setDeleteDialogOpen(false)}
        type="error"
      >
        <Dialog.Body>
          {deleteError
            || `Tem certeza que deseja excluir o modelo "${modelToDelete?.config.label}"? Se ele tiver dados, prefira "Ocultar".`}
        </Dialog.Body>
        <Dialog.Footer>
          <Button type="secondary" onClick={() => setDeleteDialogOpen(false)}>
            Cancelar
          </Button>
          {!deleteError && (
            <Button onClick={handleConfirmDelete}>
              Excluir
            </Button>
          )}
        </Dialog.Footer>
      </Dialog>
      {isMobile ? (
        <ModelsMobile
          models={sortedModels}
          isFetching={isFetching}
          pendingId={pendingId}
          feedback={feedback}
          onDismissFeedback={() => setFeedback(null)}
          onEdit={handleEdit}
          onToggleAtivo={handleToggleAtivo}
          onDelete={handleDeleteRequest}
          onCreate={handleCreate}
        />
      ) : (
        <Container>
          <Row className="align-items-center pb-4">
            <Col>
              <h1 className="mb-0">MODELOS METEOROLÓGICOS</h1>
            </Col>
            <Col className="d-flex justify-content-end">
              <Button icon="add" onClick={handleCreate}>
                Adicionar Modelo
              </Button>
            </Col>
          </Row>
          {feedback && (
            <Snackbar
              variant={feedback.variant}
              closeButton
              onClose={() => setFeedback(null)}
              className="mb-3"
            >
              {feedback.msg}
            </Snackbar>
          )}
          <Row className="pt-3">
            <ModelsTable
              models={sortedModels}
              isFetching={isFetching}
              pendingId={pendingId}
              sortBy={sortBy}
              sortDirection={sortDirection}
              setSortBy={setSortBy}
              setSortDirection={setSortDirection}
              onEdit={handleEdit}
              onToggleAtivo={handleToggleAtivo}
              onDelete={handleDeleteRequest}
            />
          </Row>
        </Container>
      )}
    </div>
  );
}
